import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Key, CheckCircle2, ExternalLink, CreditCard, Plus, Pencil, Trash2, QrCode, ExternalLink as LinkIcon } from "lucide-react";
import PaymentSystemModal from "@/components/PaymentSystemModal";

export default function SettingsPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [account, setAccount] = useState({ current_password: "", new_email: "", new_password: "", confirm_password: "" });
  const [aiKeyInfo, setAiKeyInfo] = useState(null);
  const [newKey, setNewKey] = useState("");
  const [testing, setTesting] = useState(false);
  const [landing, setLanding] = useState(null);
  const [sr, setSr] = useState(null);
  const [security, setSecurity] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [payment, setPayment] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);
  // Multi-payment-system CRUD (Super Admin adds GCash / Maya / Bank Transfer, etc.)
  const [paymentSystems, setPaymentSystems] = useState([]);
  const [psModalOpen, setPsModalOpen] = useState(false);
  const [psEditing, setPsEditing] = useState(null);
  const [psTogglingId, setPsTogglingId] = useState(null);
  // Email (Resend) settings — super-admin only
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailDraft, setEmailDraft] = useState({ resend_api_key: "", sender_email: "", sender_name: "", app_public_url: "" });
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTestTo, setEmailTestTo] = useState("");

  // Landing page — list of active courses so the super admin can toggle
  // which ones appear in the public /landing endpoint (and therefore on
  // the marketing site's Available Courses grid).
  const [landingCourses, setLandingCourses] = useState([]);

  useEffect(() => {
    if (isSuper) api.get("/settings/ai-key").then((r) => setAiKeyInfo(r.data));
    if (isSuper) api.get("/settings/email").then((r) => {
      setEmailCfg(r.data);
      setEmailDraft({
        resend_api_key: "",
        sender_email: r.data.sender_email || "",
        sender_name: r.data.sender_name || "",
        app_public_url: r.data.app_public_url || "",
      });
    }).catch(() => {});
    // Load landing settings. When the admin has never customised the
    // Exam Tracks section, we hydrate the state with the two built-in
    // defaults ("Board Exam Track" + "Civil Service Track — NEW") so the
    // admin can edit them directly — no need to click "Add Exam Track"
    // and re-type everything by hand. The pre-populated values only get
    // saved to the DB when the admin clicks "Save Landing Page".
    api.get("/settings/landing").then((r) => {
      const data = r.data || {};
      if (!Array.isArray(data.landing_tracks) || data.landing_tracks.length === 0) {
        data.landing_tracks = [
          {
            badge_label: "Board Exam Track",
            title: "Philippine Licensure Examinations",
            subtitle: "Structured review for PRC-administered board exams across multiple professions — built around the Course → Subject → Module hierarchy and real exam coverage.",
            items: [
              "Licensure Examination for Teachers (LET)",
              "Nursing Licensure Examination (NLE)",
              "Certified Public Accountant (CPALE)",
              "Criminologist Licensure Examination (CLE)",
              "Marine Deck & Engine Officer exams",
              "Civil, Mechanical, & Electrical Engineering boards",
            ],
            color_theme: "blue",
          },
          {
            badge_label: "Civil Service Track — NEW",
            title: "Civil Service Examination (CSE)",
            subtitle: "Comprehensive CSC-CSE prep — both Professional and Sub-Professional levels — including English & Filipino vocabulary, analogies, paragraph reading, numerical reasoning, and clerical aptitude.",
            items: [
              "CSE — Professional Level",
              "CSE — Sub-Professional Level",
              "Verbal Reasoning (English & Filipino)",
              "Numerical Reasoning & Problem Solving",
              "Analytical Ability & Clerical Operations",
              "General Information (Philippine Constitution, R.A. 6713, etc.)",
            ],
            color_theme: "amber",
          },
        ];
      }
      setLanding(data);
    }).catch(() => {});
    api.get("/settings/smart-review").then((r) => setSr(r.data)).catch(() => {});
    api.get("/settings/security").then((r) => setSecurity(r.data)).catch(() => {});
    api.get("/settings/tickets").then((r) => setTickets(r.data)).catch(() => {});
    api.get("/settings/payment").then((r) => setPayment(r.data)).catch(() => {});
    api.get("/settings/payment-systems").then((r) => setPaymentSystems(r.data || [])).catch(() => {});
    api.get("/courses", { params: { status: "active" } })
      .then((r) => setLandingCourses(Array.isArray(r.data) ? r.data : (r.data?.courses || [])))
      .catch(() => setLandingCourses([]));
  }, [isSuper]);

  // ---- Payment-system CRUD helpers ----
  const openAddPaymentSystem = () => { setPsEditing(null); setPsModalOpen(true); };
  const openEditPaymentSystem = (ps) => { setPsEditing(ps); setPsModalOpen(true); };
  const closePaymentSystemModal = () => { setPsModalOpen(false); setPsEditing(null); };
  const handlePaymentSystemSaved = (row) => {
    setPaymentSystems((list) => {
      const exists = list.some((x) => x.id === row.id);
      return exists ? list.map((x) => (x.id === row.id ? row : x)) : [...list, row];
    });
  };
  const togglePaymentSystem = async (ps) => {
    setPsTogglingId(ps.id);
    try {
      const next = !ps.enabled;
      await api.patch(`/settings/payment-systems/${ps.id}/toggle`, { enabled: next });
      setPaymentSystems((list) => list.map((x) => (x.id === ps.id ? { ...x, enabled: next } : x)));
      toast.success(next ? `${ps.name} enabled` : `${ps.name} disabled`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to toggle");
    } finally { setPsTogglingId(null); }
  };
  const deletePaymentSystem = async (ps) => {
    if (!window.confirm(`Delete "${ps.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/settings/payment-systems/${ps.id}`);
      setPaymentSystems((list) => list.filter((x) => x.id !== ps.id));
      toast.success("Payment system removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const saveEmailCfg = async () => {
    setSavingEmail(true);
    try {
      // Send only fields that have been edited / are present. The API treats
      // empty resend_api_key as "leave the stored key untouched".
      const payload = {
        resend_api_key: (emailDraft.resend_api_key || "").trim() || null,
        sender_email: (emailDraft.sender_email || "").trim() || null,
        sender_name: (emailDraft.sender_name || "").trim() || null,
        app_public_url: (emailDraft.app_public_url || "").trim() || null,
      };
      const r = await api.put("/settings/email", payload);
      setEmailCfg(r.data);
      setEmailDraft((d) => ({ ...d, resend_api_key: "" })); // never echo new key back
      toast.success("Email settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save email settings");
    } finally {
      setSavingEmail(false);
    }
  };

  const sendEmailTest = async () => {
    setTestingEmail(true);
    try {
      const r = await api.post("/settings/email/test", { to: (emailTestTo || "").trim() || null });
      toast.success(`Test email sent to ${r.data.to} (from ${r.data.from})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTestingEmail(false);
    }
  };

  const savePayment = async (overrides = null) => {
    if (!payment) return;
    const link = (payment.paymongo_payment_link || "").trim();
    if (link && !/^https?:\/\//i.test(link)) {
      toast.error("Payment link must start with http:// or https://");
      return;
    }
    setSavingPayment(true);
    try {
      const body = {
        paymongo_payment_link: link,
        payment_instructions: (payment.payment_instructions || "").trim(),
        payment_required_for_enrollment: !!payment.payment_required_for_enrollment,
        paymongo_enabled: !!payment.paymongo_enabled,
        ...(overrides || {}),
      };
      const r = await api.put("/settings/payment", body);
      setPayment({ ...payment, ...r.data });
      toast.success("Payment settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save payment settings");
    } finally {
      setSavingPayment(false);
    }
  };

  // Toggles auto-save so admins don't accidentally leave a change unsaved —
  // this was a common footgun with the previous "Save Payment Link" button
  // (looked like it only saved the link, so PayMongo Enabled changes never
  // persisted). Autosaving with an override ensures the exact new value ships
  // even before React re-renders `payment`.
  const togglePaymongoEnabled = async (v) => {
    setPayment((p) => ({ ...p, paymongo_enabled: v }));
    await savePayment({ paymongo_enabled: v });
  };

  const togglePaymentRequired = async (v) => {
    setPayment((p) => ({ ...p, payment_required_for_enrollment: v }));
    await savePayment({ payment_required_for_enrollment: v });
  };

  const testPaymentLink = () => {
    const link = (payment?.paymongo_payment_link || "").trim();
    if (!link) {
      toast.error("Paste a payment link first");
      return;
    }
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const saveSecurity = async (next) => {
    try {
      // Merge with existing state so a single-field save doesn't drop the
      // other fields the backend expects.
      const payload = {
        student_one_device_only:
          typeof next.student_one_device_only === "boolean"
            ? next.student_one_device_only
            : !!security?.student_one_device_only,
        auto_approve_social_signups:
          typeof next.auto_approve_social_signups === "boolean"
            ? next.auto_approve_social_signups
            : !!security?.auto_approve_social_signups,
        auto_approve_new_registrations:
          typeof next.auto_approve_new_registrations === "boolean"
            ? next.auto_approve_new_registrations
            : (security?.auto_approve_new_registrations ?? true),
      };
      const r = await api.put("/settings/security", payload);
      setSecurity({
        student_one_device_only: r.data.student_one_device_only,
        auto_approve_social_signups: r.data.auto_approve_social_signups,
        auto_approve_new_registrations: r.data.auto_approve_new_registrations,
      });
      toast.success("Security settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const saveTickets = async (next) => {
    try {
      const payload = {
        enabled: !!next.auto_archive_resolved_enabled,
        days: Number(next.auto_archive_resolved_days) || 30,
      };
      const r = await api.put("/settings/tickets", payload);
      setTickets({
        auto_archive_resolved_enabled: r.data.auto_archive_resolved_enabled,
        auto_archive_resolved_days: r.data.auto_archive_resolved_days,
      });
      toast.success("Tickets settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const saveAccount = async () => {
    try {
      const r = await api.patch("/settings/account", account);
      toast.success("Account updated");
      if (r.data.email_change_status === "pending_verification_mock") toast.info("Email change pending verification (mock).");
      setAccount({ current_password: "", new_email: "", new_password: "", confirm_password: "" });
    } catch (e) { toast.error(extractErrorMessage(e, "Failed to update account")); }
  };

  const saveKey = async () => {
    try {
      await api.put("/settings/ai-key", { api_key: newKey });
      toast.success("AI key saved");
      setNewKey("");
      const r = await api.get("/settings/ai-key"); setAiKeyInfo(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const testKey = async () => {
    setTesting(true);
    try {
      const r = await api.post("/settings/ai-key/test");
      toast.success(`Test passed (source: ${r.data.source})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  const saveLanding = async () => {
    try {
      // Clean up bullet items only at save time — trim whitespace from
      // each line and drop blank lines. This keeps the editor tolerant
      // of the admin's typing (trailing spaces, empty lines while
      // rearranging) but publishes a tidy list.
      const payload = { ...landing };
      if (Array.isArray(payload.landing_tracks)) {
        payload.landing_tracks = payload.landing_tracks.map((t) => ({
          ...t,
          items: Array.isArray(t.items)
            ? t.items.map((x) => (x || "").trim()).filter(Boolean)
            : [],
        }));
      }
      await api.put("/settings/landing", payload);
      toast.success("Landing page updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveSr = async () => {
    try {
      const payload = {
        session_size: Number(sr.session_size),
        weak_mix_percent: Number(sr.weak_mix_percent),
        new_mix_percent: Number(sr.new_mix_percent),
        retention_mix_percent: Number(sr.retention_mix_percent),
        ai_insights_enabled: Boolean(sr.ai_insights_enabled),
      };
      const total = payload.weak_mix_percent + payload.new_mix_percent + payload.retention_mix_percent;
      if (total !== 100) {
        toast.error(`Mix must total 100% (currently ${total}%)`);
        return;
      }
      const r = await api.put("/settings/smart-review", payload);
      setSr(r.data);
      toast.success("Smart Review settings updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Configuration</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Settings</h1>
      </div>
      <Tabs defaultValue="account" className="w-full">
        <TabsList>
          <TabsTrigger value="account" data-testid="tab-account">Account</TabsTrigger>
          <TabsTrigger value="ai" disabled={!isSuper} data-testid="tab-ai-key">AI Key</TabsTrigger>
          <TabsTrigger value="email" disabled={!isSuper} data-testid="tab-email">Email</TabsTrigger>
          <TabsTrigger value="smart-review" disabled={!isSuper} data-testid="tab-smart-review">Smart Review</TabsTrigger>
          <TabsTrigger value="landing" data-testid="tab-landing">Landing Page</TabsTrigger>
          <TabsTrigger value="payment" data-testid="tab-payment">Payment</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="account" className="mt-6">
          <Card className="p-6 border-slate-200 max-w-2xl">
            <h3 className="font-display text-lg font-bold mb-1">Account Settings</h3>
            <p className="text-sm text-slate-600 mb-4">Update your email or password. Current password is required.</p>
            <div className="space-y-4">
              <div><Label>Current Email</Label><Input value={user.email} disabled /></div>
              <div><Label>Current Password *</Label><Input type="password" autoComplete="new-password" value={account.current_password} onChange={(e) => setAccount({ ...account, current_password: e.target.value })} data-testid="current-pw" /></div>
              <div><Label>New Email</Label><Input type="email" autoComplete="off" value={account.new_email} onChange={(e) => setAccount({ ...account, new_email: e.target.value })} placeholder="Leave blank to keep current" data-testid="new-email" /></div>
              <div><Label>New Password</Label><Input type="password" autoComplete="new-password" value={account.new_password} onChange={(e) => setAccount({ ...account, new_password: e.target.value })} placeholder="Leave blank to keep current" data-testid="new-pw" /></div>
              <div><Label>Confirm New Password</Label><Input type="password" autoComplete="new-password" value={account.confirm_password} onChange={(e) => setAccount({ ...account, confirm_password: e.target.value })} data-testid="confirm-pw" /></div>
              <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={saveAccount} data-testid="save-account-btn">Save Changes</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="ai" className="mt-6">
          {isSuper && (
            <Card className="p-6 border-slate-200 max-w-2xl">
              <h3 className="font-display text-lg font-bold mb-1">OpenAI API Key</h3>
              <p className="text-sm text-slate-600 mb-4">Stored securely on the backend. When set, this overrides the Emergent Universal Key.</p>
              {aiKeyInfo && (
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200 mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2"><Key className="w-4 h-4 text-slate-500" /><span className="text-sm font-mono">{aiKeyInfo.masked || "(no admin key set)"}</span></div>
                  {aiKeyInfo.is_set && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                </div>
              )}
              <div className="space-y-4">
                <div><Label>New API Key</Label><Input type="password" value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="sk-..." data-testid="ai-key-input" /></div>
                <div className="flex gap-2 flex-wrap">
                  <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={saveKey} disabled={!newKey} data-testid="save-key-btn">Save</Button>
                  <Button variant="outline" onClick={testKey} disabled={testing} data-testid="test-key-btn">{testing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Test Connection"}</Button>
                </div>
                <p className="text-xs text-slate-500">{aiKeyInfo?.fallback}</p>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="email" className="mt-6">
          {isSuper && emailCfg && (
            <Card className="p-6 border-slate-200 max-w-2xl" data-testid="email-settings-card">
              <h3 className="font-display text-lg font-bold mb-1">Email (Resend)</h3>
              <p className="text-sm text-slate-600 mb-4">
                Sender, API key, and reply-to domain for transactional emails (verification, password reset).
                These override the values from the deploy environment and take effect immediately.
              </p>

              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 mb-4 flex items-center justify-between" data-testid="email-current-key">
                <div className="flex items-center gap-2">
                  <Key className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-mono">{emailCfg.resend_api_key_masked || "(no key set)"}</span>
                </div>
                {emailCfg.resend_api_key_set && <CheckCircle2 className="w-4 h-4 text-green-600" />}
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Resend API Key</Label>
                  <Input
                    type="password"
                    value={emailDraft.resend_api_key}
                    onChange={(e) => setEmailDraft({ ...emailDraft, resend_api_key: e.target.value })}
                    placeholder="Leave blank to keep current key"
                    autoComplete="off"
                    data-testid="email-api-key-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Find / rotate keys at <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">resend.com/api-keys</a>.</p>
                </div>
                <div>
                  <Label>Sender Email</Label>
                  <Input
                    type="email"
                    value={emailDraft.sender_email}
                    onChange={(e) => setEmailDraft({ ...emailDraft, sender_email: e.target.value })}
                    placeholder="noreply@support.prepmaster.ph"
                    data-testid="email-sender-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Must be on a domain you've verified at <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">resend.com/domains</a>.</p>
                </div>
                <div>
                  <Label>Sender Name</Label>
                  <Input
                    type="text"
                    value={emailDraft.sender_name}
                    onChange={(e) => setEmailDraft({ ...emailDraft, sender_name: e.target.value })}
                    placeholder="PrepMasters"
                    data-testid="email-sender-name-input"
                  />
                </div>
                <div>
                  <Label>App Public URL (used in email links)</Label>
                  <Input
                    type="url"
                    value={emailDraft.app_public_url}
                    onChange={(e) => setEmailDraft({ ...emailDraft, app_public_url: e.target.value })}
                    placeholder="https://prepmaster.ph"
                    data-testid="email-app-url-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Verification + reset links use this as their base URL (e.g. <code>{emailDraft.app_public_url || "https://prepmaster.ph"}/verify-email?token=…</code>).</p>
                </div>
                <div className="flex gap-2 flex-wrap pt-1">
                  <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={saveEmailCfg} disabled={savingEmail} data-testid="email-save-btn">
                    {savingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                    Save Email Settings
                  </Button>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-slate-200">
                <h4 className="font-semibold text-sm">Send a test email</h4>
                <p className="text-xs text-slate-500 mt-1 mb-3">Sends a small connectivity-test email using the saved settings. Leave the recipient blank to send it to your own account email.</p>
                <div className="flex gap-2 flex-wrap items-end">
                  <div className="flex-1 min-w-[200px]">
                    <Label>Recipient (optional)</Label>
                    <Input
                      type="email"
                      value={emailTestTo}
                      onChange={(e) => setEmailTestTo(e.target.value)}
                      placeholder={user.email}
                      data-testid="email-test-to-input"
                    />
                  </div>
                  <Button variant="outline" onClick={sendEmailTest} disabled={testingEmail} data-testid="email-test-btn">
                    {testingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ExternalLink className="w-4 h-4 mr-1.5" />}
                    Send test email
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="smart-review" className="mt-6">
          {isSuper && sr && (
            <Card className="p-6 border-slate-200 max-w-2xl" data-testid="smart-review-settings-card">
              <h3 className="font-display text-lg font-bold mb-1">Smart Review Configuration</h3>
              <p className="text-sm text-slate-600 mb-4">Tune the AI-powered spaced repetition engine.</p>
              <div className="space-y-4">
                <div>
                  <Label>Session Size</Label>
                  <Input type="number" min="5" max="50" value={sr.session_size}
                         onChange={(e) => setSr({ ...sr, session_size: e.target.value })}
                         data-testid="sr-session-size" />
                  <p className="text-xs text-slate-500 mt-1">Questions per Smart Review session (5–50).</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Weak / Missed %</Label>
                    <Input type="number" min="0" max="100" value={sr.weak_mix_percent}
                           onChange={(e) => setSr({ ...sr, weak_mix_percent: e.target.value })}
                           data-testid="sr-weak-pct" />
                  </div>
                  <div>
                    <Label>New Questions %</Label>
                    <Input type="number" min="0" max="100" value={sr.new_mix_percent}
                           onChange={(e) => setSr({ ...sr, new_mix_percent: e.target.value })}
                           data-testid="sr-new-pct" />
                  </div>
                  <div>
                    <Label>Retention %</Label>
                    <Input type="number" min="0" max="100" value={sr.retention_mix_percent}
                           onChange={(e) => setSr({ ...sr, retention_mix_percent: e.target.value })}
                           data-testid="sr-retention-pct" />
                  </div>
                </div>
                <p className="text-xs text-slate-500">Three percentages must total 100. Current sum: {Number(sr.weak_mix_percent) + Number(sr.new_mix_percent) + Number(sr.retention_mix_percent)}%</p>
                <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
                  <div>
                    <div className="font-medium text-sm">AI Learning Insights</div>
                    <div className="text-xs text-slate-500">Auto-generate AI insight after each Smart Review session.</div>
                  </div>
                  <Switch checked={!!sr.ai_insights_enabled}
                          onCheckedChange={(v) => setSr({ ...sr, ai_insights_enabled: v })}
                          data-testid="sr-insights-toggle" />
                </div>
                <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={saveSr} data-testid="save-sr-btn">Save Smart Review Settings</Button>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="landing" className="mt-6">
          {landing && (
            <Card className="p-6 border-slate-200 max-w-3xl">
              <h3 className="font-display text-lg font-bold mb-1">Landing Page Content</h3>
              <p className="text-sm text-slate-600 mb-4">Edit the public landing page text.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>App name</Label><Input value={landing.app_name || ""} onChange={(e) => setLanding({ ...landing, app_name: e.target.value })} data-testid="lp-app-name" /></div>
                <div><Label>Tagline</Label><Input value={landing.tagline || ""} onChange={(e) => setLanding({ ...landing, tagline: e.target.value })} /></div>
                <div className="sm:col-span-2"><Label>Hero Title</Label><Textarea rows={2} value={landing.hero_title || ""} onChange={(e) => setLanding({ ...landing, hero_title: e.target.value })} /></div>
                <div className="sm:col-span-2"><Label>Hero Subtitle</Label><Textarea rows={3} value={landing.hero_subtitle || ""} onChange={(e) => setLanding({ ...landing, hero_subtitle: e.target.value })} /></div>
                <div><Label>Contact Email</Label><Input value={landing.contact_email || ""} onChange={(e) => setLanding({ ...landing, contact_email: e.target.value })} /></div>
                <div><Label>Contact Number</Label><Input value={landing.contact_number || ""} onChange={(e) => setLanding({ ...landing, contact_number: e.target.value })} /></div>
                <div><Label>Facebook Link</Label><Input value={landing.facebook_link || ""} onChange={(e) => setLanding({ ...landing, facebook_link: e.target.value })} /></div>
                <div><Label>Address</Label><Input value={landing.address || ""} onChange={(e) => setLanding({ ...landing, address: e.target.value })} /></div>
                <div className="sm:col-span-2">
                  <Label>Privacy Policy</Label>
                  <p className="text-[11px] text-slate-500 mb-1">Shown on /legal/privacy. Plain text — newlines and paragraphs are preserved.</p>
                  <Textarea rows={12} value={landing.privacy_policy || ""} onChange={(e) => setLanding({ ...landing, privacy_policy: e.target.value })} className="font-mono text-xs leading-relaxed" data-testid="lp-privacy-policy" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Terms of Use</Label>
                  <p className="text-[11px] text-slate-500 mb-1">Shown on /legal/terms. Plain text — newlines and paragraphs are preserved.</p>
                  <Textarea rows={12} value={landing.terms_of_use || ""} onChange={(e) => setLanding({ ...landing, terms_of_use: e.target.value })} className="font-mono text-xs leading-relaxed" data-testid="lp-terms-of-use" />
                </div>
                <div className="sm:col-span-2"><Label>Footer Text</Label><Input value={landing.footer_text || ""} onChange={(e) => setLanding({ ...landing, footer_text: e.target.value })} /></div>
              </div>

              {/* Visible Courses on Landing Page — checkbox list.
                  null / empty list of ids = "show every active course" (default).
                  Non-empty list = whitelist. Empty explicit list = hide all. */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h4 className="font-display font-bold text-base">Courses Visible on Landing Page</h4>
                <p className="text-sm text-slate-600 mt-1 mb-3">
                  Uncheck any course that's not ready for public sign-ups. Unchecked courses are hidden from the public "Available Courses" grid. Leave everything checked to show all active courses.
                </p>
                {landingCourses.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">No active courses found. Create one first in the Courses tab.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-2">
                    {landingCourses.map((c) => {
                      const list = Array.isArray(landing.landing_visible_course_ids) ? landing.landing_visible_course_ids : null;
                      const checked = list === null ? true : list.includes(c.id);
                      const onToggle = (v) => {
                        // Start from "all active courses" whenever the admin
                        // has never explicitly saved a whitelist, so
                        // unchecking one course produces the expected result.
                        const base = list === null ? landingCourses.map((x) => x.id) : list;
                        const next = v
                          ? Array.from(new Set([...base, c.id]))
                          : base.filter((x) => x !== c.id);
                        setLanding({ ...landing, landing_visible_course_ids: next });
                      };
                      return (
                        <label key={c.id} className="flex items-start gap-2 p-2 rounded-md border border-slate-200 bg-white cursor-pointer hover:bg-slate-50" data-testid={`lp-course-toggle-${c.id}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => onToggle(e.target.checked)}
                            className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500"
                          />
                          <span className="text-sm">
                            <span className="font-medium text-slate-900">{c.course_name}</span>
                            {c.description && <span className="block text-[11px] text-slate-500 line-clamp-2">{c.description}</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {Array.isArray(landing.landing_visible_course_ids) && landing.landing_visible_course_ids.length === 0 && (
                  <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    Warning: no courses selected — the "Available Courses" section will be hidden entirely from the public landing page.
                  </div>
                )}
              </div>

              {/* Promotional course cards — admin can add arbitrary cards
                  that appear next to the live DB courses. Great for
                  teasing an upcoming course before its content is imported. */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h4 className="font-display font-bold text-base">Additional Course Cards</h4>
                <p className="text-sm text-slate-600 mt-1 mb-3">
                  Manually add course cards to the landing page's "Available Courses" grid — useful for programs that don't yet exist as a full course in the platform, or for promotional cross-listing.
                </p>
                <div className="space-y-3">
                  {(landing.landing_extra_courses || []).map((c, i) => (
                    <div key={i} className="p-3 rounded-md border border-slate-200 bg-slate-50" data-testid={`lp-extra-course-${i}`}>
                      <div className="grid sm:grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Title</Label>
                          <Input
                            value={c.title || ""}
                            onChange={(e) => {
                              const list = [...(landing.landing_extra_courses || [])];
                              list[i] = { ...list[i], title: e.target.value };
                              setLanding({ ...landing, landing_extra_courses: list });
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Short Description</Label>
                          <Input
                            value={c.description || ""}
                            onChange={(e) => {
                              const list = [...(landing.landing_extra_courses || [])];
                              list[i] = { ...list[i], description: e.target.value };
                              setLanding({ ...landing, landing_extra_courses: list });
                            }}
                          />
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 mt-2"
                        onClick={() => {
                          const list = [...(landing.landing_extra_courses || [])];
                          list.splice(i, 1);
                          setLanding({ ...landing, landing_extra_courses: list });
                        }}
                        data-testid={`lp-extra-course-delete-${i}`}
                      >
                        Delete card
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const list = [...(landing.landing_extra_courses || []), { title: "New Course", description: "" }];
                      setLanding({ ...landing, landing_extra_courses: list });
                    }}
                    data-testid="lp-extra-course-add"
                  >
                    + Add Course Card
                  </Button>
                </div>
              </div>

              {/* Exam Tracks — the two large gradient callouts above the
                  course grid. Admin can add or remove tracks, edit their
                  title / subtitle / bullet items, and pick a color. */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h4 className="font-display font-bold text-base">Exam Tracks (Landing Callouts)</h4>
                <p className="text-sm text-slate-600 mt-1 mb-3">
                  These are the two large gradient cards on the landing page — <span className="font-semibold">Board Exam Track</span> and <span className="font-semibold">Civil Service Track — NEW</span>. Edit any field below and click <span className="font-semibold">Save Landing Page</span> to publish. Delete a card to remove it entirely, or click <span className="font-semibold">Add Exam Track</span> to create a third one.
                </p>
                <div className="space-y-3">
                  {(landing.landing_tracks || []).map((t, i) => (
                    <div key={i} className="p-3 rounded-md border border-slate-200 bg-slate-50" data-testid={`lp-track-${i}`}>
                      <div className="grid sm:grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Badge label</Label>
                          <Input
                            value={t.badge_label || ""}
                            onChange={(e) => {
                              const list = [...(landing.landing_tracks || [])];
                              list[i] = { ...list[i], badge_label: e.target.value };
                              setLanding({ ...landing, landing_tracks: list });
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Color theme</Label>
                          <select
                            value={t.color_theme || "blue"}
                            onChange={(e) => {
                              const list = [...(landing.landing_tracks || [])];
                              list[i] = { ...list[i], color_theme: e.target.value };
                              setLanding({ ...landing, landing_tracks: list });
                            }}
                            className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                          >
                            <option value="blue">Blue</option>
                            <option value="amber">Amber</option>
                            <option value="emerald">Emerald</option>
                            <option value="violet">Violet</option>
                          </select>
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-xs">Title</Label>
                          <Input
                            value={t.title || ""}
                            onChange={(e) => {
                              const list = [...(landing.landing_tracks || [])];
                              list[i] = { ...list[i], title: e.target.value };
                              setLanding({ ...landing, landing_tracks: list });
                            }}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-xs">Subtitle / description</Label>
                          <Textarea
                            rows={3}
                            value={t.subtitle || ""}
                            onChange={(e) => {
                              const list = [...(landing.landing_tracks || [])];
                              list[i] = { ...list[i], subtitle: e.target.value };
                              setLanding({ ...landing, landing_tracks: list });
                            }}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-xs">Bullet items (one per line)</Label>
                          <Textarea
                            rows={6}
                            value={(t.items || []).join("\n")}
                            onChange={(e) => {
                              // Preserve the RAW input as-typed — do NOT
                              // trim or filter here. Trimming on every
                              // keystroke swallows trailing spaces so the
                              // admin cannot type multi-word items like
                              // "Real Estate Brokers Licensure Exam".
                              // Clean-up happens once on Save (`saveLanding`).
                              const list = [...(landing.landing_tracks || [])];
                              list[i] = { ...list[i], items: e.target.value.split("\n") };
                              setLanding({ ...landing, landing_tracks: list });
                            }}
                          />
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 mt-2"
                        onClick={() => {
                          const list = [...(landing.landing_tracks || [])];
                          list.splice(i, 1);
                          setLanding({ ...landing, landing_tracks: list });
                        }}
                        data-testid={`lp-track-delete-${i}`}
                      >
                        Delete track
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const list = [...(landing.landing_tracks || []), {
                        badge_label: "New Track",
                        title: "",
                        subtitle: "",
                        items: [],
                        color_theme: "blue",
                      }];
                      setLanding({ ...landing, landing_tracks: list });
                    }}
                    data-testid="lp-track-add"
                  >
                    + Add Exam Track
                  </Button>
                </div>
              </div>

              <Button className="mt-6 bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={saveLanding} data-testid="save-landing-btn">Save Landing Page</Button>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="payment" className="mt-6">
          {payment ? (
            <Card className="p-6 border-slate-200 max-w-3xl" data-testid="payment-settings-card">
              <h3 className="font-display text-lg font-bold mb-1 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-600" /> Payment Settings
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-5">
                Configure the PayMongo payment link students will use during course enrollment. Only the Super Admin can edit these values.
              </p>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="paymongo_link">PayMongo Payment Link</Label>
                  <Input
                    id="paymongo_link"
                    value={payment.paymongo_payment_link || ""}
                    onChange={(e) => setPayment({ ...payment, paymongo_payment_link: e.target.value })}
                    placeholder="https://paymongo.page/l/prepmaster-enrollment"
                    className="mt-1.5"
                    data-testid="paymongo-link-input"
                    disabled={!isSuper}
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Paste the full PayMongo Payment Link URL (starts with https://).</p>
                </div>

                <div>
                  <Label htmlFor="payment_instructions">Payment Instructions</Label>
                  <Textarea
                    id="payment_instructions"
                    rows={4}
                    value={payment.payment_instructions || ""}
                    onChange={(e) => setPayment({ ...payment, payment_instructions: e.target.value })}
                    className="mt-1.5"
                    placeholder="Tell students how to pay and upload proof of payment."
                    data-testid="payment-instructions-input"
                    disabled={!isSuper}
                  />
                </div>

                <div className="flex items-start justify-between gap-4 p-4 bg-emerald-50/50 dark:bg-emerald-950/20 rounded-md border-2 border-emerald-200 dark:border-emerald-800">
                  <div>
                    <Label className="text-sm font-semibold flex items-center gap-1.5">
                      <CreditCard className="w-4 h-4 text-emerald-700 dark:text-emerald-300" />
                      Enable PayMongo Payment
                    </Label>
                    <p className="text-[11px] text-slate-600 dark:text-slate-300 mt-1">
                      {payment.paymongo_enabled
                        ? "ON — PayMongo is available to students as a payment option."
                        : "OFF — PayMongo is hidden from students. They must use one of the payment systems below."}
                    </p>
                  </div>
                  <Switch
                    checked={!!payment.paymongo_enabled}
                    onCheckedChange={togglePaymongoEnabled}
                    data-testid="paymongo-enabled-toggle"
                    disabled={!isSuper || savingPayment}
                  />
                </div>

                <div className="flex items-start justify-between gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-md border border-slate-200">
                  <div>
                    <Label className="text-sm font-semibold">Require payment before course enrollment</Label>
                    <p className="text-[11px] text-slate-500 mt-1">
                      When ON, students must complete payment and upload proof before submitting an enrollment request.
                    </p>
                  </div>
                  <Switch
                    checked={!!payment.payment_required_for_enrollment}
                    onCheckedChange={togglePaymentRequired}
                    data-testid="payment-required-toggle"
                    disabled={!isSuper || savingPayment}
                  />
                </div>

                {isSuper && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      className="bg-[#1E3A8A] hover:bg-[#1E40AF]"
                      onClick={() => savePayment()}
                      disabled={savingPayment}
                      data-testid="save-payment-settings-btn"
                    >
                      {savingPayment ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Save Payment Settings
                    </Button>
                    <Button variant="outline" onClick={testPaymentLink} data-testid="test-payment-link-btn">
                      <ExternalLink className="w-4 h-4 mr-2" /> Test Payment Link
                    </Button>
                  </div>
                )}
                {!isSuper && (
                  <p className="text-xs text-amber-700">Only the Super Admin can edit payment settings.</p>
                )}
              </div>
            </Card>
          ) : (
            <div className="text-sm text-slate-400">Loading…</div>
          )}

          {isSuper && (
            <Card className="p-6 border-slate-200 max-w-3xl mt-6" data-testid="payment-systems-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-lg font-bold mb-1">Payment Systems</h3>
                  <p className="text-sm text-slate-600 mb-1">
                    Configure the payment methods students can use to pay (GCash, Maya, Bank Transfer, etc.).
                    Toggle a method off to hide it from students without deleting it.
                  </p>
                </div>
                <Button
                  className="bg-[#1E3A8A] hover:bg-[#1E40AF] shrink-0"
                  onClick={openAddPaymentSystem}
                  data-testid="add-payment-system-btn"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  Add Payment System
                </Button>
              </div>

              <div className="mt-5 space-y-3">
                {paymentSystems.length === 0 ? (
                  <div className="p-6 rounded-md border-2 border-dashed border-slate-200 dark:border-slate-700 text-center">
                    <QrCode className="w-8 h-8 mx-auto text-slate-400" strokeWidth={1.5} />
                    <p className="text-sm text-slate-600 mt-2">No payment systems yet.</p>
                    <p className="text-xs text-slate-500 mt-0.5">Click "Add Payment System" to get started.</p>
                  </div>
                ) : (
                  paymentSystems.map((ps) => (
                    <div
                      key={ps.id}
                      className={`p-4 rounded-md border flex items-start gap-3 ${
                        ps.enabled
                          ? "border-slate-200 bg-white dark:bg-slate-900/40 dark:border-slate-700"
                          : "border-slate-200 bg-slate-50/70 dark:bg-slate-800/40 dark:border-slate-700 opacity-70"
                      }`}
                      data-testid={`payment-system-row-${ps.id}`}
                    >
                      <div className="w-16 h-16 rounded-md border border-slate-200 dark:border-slate-700 grid place-items-center bg-white dark:bg-slate-900 overflow-hidden shrink-0">
                        {ps.logo_data_url ? (
                          <img src={ps.logo_data_url} alt={`${ps.name} logo`} className="w-full h-full object-contain" />
                        ) : ps.qr_code_data_url ? (
                          <img src={ps.qr_code_data_url} alt={`${ps.name} QR`} className="w-full h-full object-contain" />
                        ) : (
                          <QrCode className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 dark:text-slate-100 truncate" data-testid={`ps-name-${ps.id}`}>
                            {ps.name}
                          </span>
                          {!ps.enabled && (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-200 dark:bg-slate-700 dark:text-slate-300 rounded px-1.5 py-0.5">
                              Disabled
                            </span>
                          )}
                        </div>
                        {(ps.account_name || ps.account_number) && (
                          <div className="text-[11px] text-slate-600 dark:text-slate-300 mt-0.5" data-testid={`ps-account-${ps.id}`}>
                            {ps.account_name && (<><span className="font-medium">Account:</span> {ps.account_name}</>)}
                            {ps.account_name && ps.account_number && <span className="mx-1 text-slate-400">·</span>}
                            {ps.account_number && (<span className="font-mono">{ps.account_number}</span>)}
                          </div>
                        )}
                        {ps.link && (
                          <a
                            href={ps.link}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mt-0.5"
                            data-testid={`ps-link-${ps.id}`}
                          >
                            <LinkIcon className="w-3 h-3" />
                            {ps.link.length > 60 ? ps.link.slice(0, 60) + "…" : ps.link}
                          </a>
                        )}
                        {ps.instructions && (
                          <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 whitespace-pre-line line-clamp-2">
                            {ps.instructions}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <Switch
                          checked={!!ps.enabled}
                          disabled={psTogglingId === ps.id}
                          onCheckedChange={() => togglePaymentSystem(ps)}
                          data-testid={`ps-toggle-${ps.id}`}
                        />
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => openEditPaymentSystem(ps)}
                            data-testid={`ps-edit-${ps.id}`}
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                            onClick={() => deletePaymentSystem(ps)}
                            data-testid={`ps-delete-${ps.id}`}
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <PaymentSystemModal
                open={psModalOpen}
                initial={psEditing}
                onClose={closePaymentSystemModal}
                onSaved={handlePaymentSystemSaved}
              />
            </Card>
          )}
        </TabsContent>

        <TabsContent value="security" className="mt-6">
          <Card className="p-6 border-slate-200 max-w-2xl" data-testid="security-settings-card">
            <h3 className="font-display text-lg font-bold mb-1">Student Device Login Control</h3>
            <p className="text-sm text-slate-600 mb-5">
              When enabled, a student account can only stay logged in on one device at a time. Logging in on a new device automatically logs out the previous device. This setting applies only to student accounts.
            </p>
            {security ? (
              <div className="flex items-start justify-between gap-4 p-4 bg-slate-50 rounded-md border border-slate-200">
                <div>
                  <Label className="text-sm font-semibold">Allow only one active device per student account</Label>
                  <p className="text-[11px] text-slate-500 mt-1">Recommended: On — prevents account sharing.</p>
                </div>
                <Switch
                  checked={!!security.student_one_device_only}
                  onCheckedChange={(v) => saveSecurity({ student_one_device_only: v })}
                  data-testid="one-device-toggle"
                />
              </div>
            ) : (
              <div className="text-sm text-slate-400">Loading…</div>
            )}
          </Card>

          {isSuper && (
            <Card className="p-6 border-slate-200 max-w-2xl mt-6" data-testid="social-signup-approval-card">
              <h3 className="font-display text-lg font-bold mb-1">Social Sign-Up Approval</h3>
              <p className="text-sm text-slate-600 mb-5">
                Controls what happens when a new student signs up via <span className="font-semibold">Continue with Google</span> or
                <span className="font-semibold"> Continue with Apple</span>. When this is off (recommended), social sign-ups land in
                <span className="font-semibold"> Pending Students</span> and require your approval — matching the standard email/password flow.
                When on, they are approved instantly and go straight to the app.
              </p>
              {security ? (
                <div className="flex items-start justify-between gap-4 p-4 bg-slate-50 rounded-md border border-slate-200">
                  <div>
                    <Label className="text-sm font-semibold">Auto-approve Google &amp; Apple sign-ups</Label>
                    <p className="text-[11px] text-slate-500 mt-1">
                      {security.auto_approve_social_signups
                        ? "On — New Google/Apple sign-ups are approved instantly. No admin review."
                        : "Off (recommended) — New Google/Apple sign-ups need admin approval before they can log in."}
                    </p>
                  </div>
                  <Switch
                    checked={!!security.auto_approve_social_signups}
                    onCheckedChange={(v) => saveSecurity({ auto_approve_social_signups: v })}
                    data-testid="auto-approve-social-toggle"
                  />
                </div>
              ) : (
                <div className="text-sm text-slate-400">Loading…</div>
              )}
            </Card>
          )}

          {/* Unified "auto-approve new registrations" toggle that applies to
              BOTH email/password AND Google/Apple new sign-ups. Sits next to
              the social-only toggle above so an admin can see both knobs. */}
          {isSuper && (
            <Card className="p-6 border-slate-200 max-w-2xl mt-6" data-testid="auto-approve-registrations-card">
              <h3 className="font-display text-lg font-bold mb-1">Auto-approve New Student Registrations</h3>
              <p className="text-sm text-slate-600 mb-5">
                Controls what happens after a new student verifies their email (or completes a Google/Apple sign-up).
                When <span className="font-semibold">on</span>, they are auto-approved and can sign in right away.
                When <span className="font-semibold">off</span>, they land in the Enrollment Requests / Temporary Access
                queue for you to review before granting access.
              </p>
              {security ? (
                <div className="flex items-start justify-between gap-4 p-4 bg-slate-50 rounded-md border border-slate-200">
                  <div>
                    <Label className="text-sm font-semibold">Auto-approve new registrations</Label>
                    <p className="text-[11px] text-slate-500 mt-1">
                      {(security.auto_approve_new_registrations ?? true)
                        ? "On — Students who verify their email or sign up with Google/Apple can log in immediately."
                        : "Off — All new sign-ups land as pending and need admin approval before they can log in."}
                    </p>
                  </div>
                  <Switch
                    checked={security.auto_approve_new_registrations !== false}
                    onCheckedChange={(v) => saveSecurity({ auto_approve_new_registrations: v })}
                    data-testid="auto-approve-registrations-toggle"
                  />
                </div>
              ) : (
                <div className="text-sm text-slate-400">Loading…</div>
              )}
            </Card>
          )}

          {isSuper && (
            <Card className="p-6 border-slate-200 max-w-2xl mt-6" data-testid="tickets-settings-card">
              <h3 className="font-display text-lg font-bold mb-1">Support Tickets Auto-Archive</h3>
              <p className="text-sm text-slate-600 mb-5">
                Automatically move <span className="font-semibold">resolved</span> support tickets older than the chosen window
                into an archived state. Archived tickets are hidden from the default admin queue but preserved in the database —
                nothing is deleted.
              </p>
              {tickets ? (
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-4 p-4 bg-slate-50 rounded-md border border-slate-200">
                    <div>
                      <Label className="text-sm font-semibold">Auto-archive resolved tickets</Label>
                      <p className="text-[11px] text-slate-500 mt-1">Keeps your Support queue lean. Toggle off any time — archived tickets stay archived until you reopen them.</p>
                    </div>
                    <Switch
                      checked={!!tickets.auto_archive_resolved_enabled}
                      onCheckedChange={(v) => saveTickets({
                        auto_archive_resolved_enabled: v,
                        auto_archive_resolved_days: tickets.auto_archive_resolved_days,
                      })}
                      data-testid="auto-archive-toggle"
                    />
                  </div>
                  <div className={`flex items-center justify-between gap-4 p-4 rounded-md border ${tickets.auto_archive_resolved_enabled ? "bg-white border-slate-200" : "bg-slate-50 border-slate-200 opacity-60"}`}>
                    <div>
                      <Label className="text-sm font-semibold">Archive after</Label>
                      <p className="text-[11px] text-slate-500 mt-1">Tickets with status = resolved and untouched for this many days will be archived.</p>
                    </div>
                    <select
                      value={tickets.auto_archive_resolved_days}
                      disabled={!tickets.auto_archive_resolved_enabled}
                      onChange={(e) => saveTickets({
                        auto_archive_resolved_enabled: tickets.auto_archive_resolved_enabled,
                        auto_archive_resolved_days: Number(e.target.value),
                      })}
                      className="h-10 px-3 rounded-md border border-slate-300 bg-white text-slate-900 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:cursor-not-allowed disabled:bg-slate-100"
                      data-testid="auto-archive-days-select"
                    >
                      {[7, 14, 30, 60, 90, 180].map((d) => (
                        <option key={d} value={d}>{d} days</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-400">Loading…</div>
              )}
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
